import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface CreateDataPayload {
    name: string;
    code: string;
    status: 'active' | 'inactive';
    image?: File | string;
}

interface PaymentBank {
    id: string;
    name: string;
    code: string;
    status: 'active' | 'inactive';
    image?: string;
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

@Component({
  selector: 'app-payment-bank',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './payment-bank.component.html',
  styleUrl: './payment-bank.component.scss'
})
export class PaymentBankComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    selectedPaymentBankId: string = '';
    selectedImage: File | null = null;
    imagePreview: string | null = null;

    paymentBank: PaymentBank[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_page: 1
    };

    createDataForm: CreateDataPayload = {
        name: '',
        code: '',
        status: 'inactive'
    };

    constructor(private apiService: ApiService, private router: Router) { }

    ngOnInit(): void {
        this.getPaymentBank();
    }

    async getPaymentBank(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/payment-bank/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log('Payment Banks:', response);
            this.paymentBank = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            console.error('Error fetching payment banks:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Failed to fetch payment banks.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentBankById(paymentBankId: string): Promise<void> {
        try {
            const response = await this.apiService.get(`/api/v1/tenant/manage/payment-bank/${paymentBankId}`);
            // console.log('Payment Bank Details:', response);
            this.createDataForm = {
                name: response.data.name,
                code: response.data.code,
                status: response.data.status,
                image: response.data.image
            }
            // Set image preview if image exists
            if (response.data.image) {
                this.imagePreview = response.data.image;
            }
            this.selectedPaymentBankId = paymentBankId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment bank.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async createData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('name', this.createDataForm.name);
            formData.append('code', this.createDataForm.code);
            formData.append('status', this.createDataForm.status);

            // Append image if selected
            if (this.selectedImage) {
                formData.append('image', this.selectedImage);
            }

            await this.apiService.post('/api/v1/tenant/manage/payment-bank', formData);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment bank berhasil ditambahkan.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentBank();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat menambahkan payment gateway.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    async updateData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('name', this.createDataForm.name);
            formData.append('code', this.createDataForm.code);
            formData.append('status', this.createDataForm.status);

            // Append image if new image is selected
            if (this.selectedImage) {
                formData.append('image', this.selectedImage);
            }

            await this.apiService.put(`/api/v1/tenant/manage/payment-bank/${this.selectedPaymentBankId}`, formData);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment bank berhasil diperbarui.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentBank();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memperbarui payment bank.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        } finally {
            this.isSubmitting = false;
        }
    }

    async deleteData(paymentBankId: string, paymentGatewayName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus payment bank ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });
        if (result.isConfirmed) {
            try {
                await this.apiService.delete(`/api/v1/tenant/manage/payment-bank`, paymentBankId);
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Payment bank berhasil dihapus.',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false
                });
                this.getPaymentBank();
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.error?.message || 'Terjadi kesalahan saat menghapus payment bank.',
                    confirmButtonColor: '#dc3545',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        }
    }

    openaddModal(): void {
        this.isModalOpen = true;
        this.resetForm();
    }

    openeditModal(paymentBankId: string): void {
        this.getPaymentBankById(paymentBankId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.createDataForm = {
            name: '',
            code: '',
            status: 'inactive'
        };
        this.selectedImage = null;
        this.imagePreview = null;
        this.isEditMode = false;
    }

    validateDataForm(): boolean {
        if (!this.createDataForm.name || !this.createDataForm.code || !this.createDataForm.status) {
            Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: 'Semua field harus diisi.',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }
        return true;
    }

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.total_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getPaymentBank();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== IMAGE HANDLER ====================

    onImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Peringatan',
                    text: 'Format file harus berupa gambar (JPEG, JPG, PNG, atau GIF).',
                    confirmButtonColor: '#ffc107',
                });
                return;
            }

            // Validate file size (max 2MB)
            const maxSize = 2 * 1024 * 1024; // 2MB in bytes
            if (file.size > maxSize) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Peringatan',
                    text: 'Ukuran file maksimal 2MB.',
                    confirmButtonColor: '#ffc107',
                });
                return;
            }

            this.selectedImage = file;

            // Create preview
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
                this.imagePreview = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        }
    }

    removeImage(): void {
        this.selectedImage = null;
        this.imagePreview = null;
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateData();
        } else {
            await this.createData();
        }
    }
}
